import { useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../components/card';
import { Button } from '../components/button';
import { Badge } from '../components/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/table';
import { Calendar, Clock, LogIn, LogOut, TrendingUp } from 'lucide-react';
import { User } from '../models/User';
import { toast } from 'sonner';
import { ClockService } from '../services/ClockService';
import { Clocks } from '../models/Clocks';

interface EmployeeDashboardProps {
    user: User;
}

function utcToLocal(dateString: Date): Date {
    const utcDate = new Date(dateString);
    return new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * -60000);
}

function decimalToHMS(decimalHours: number): string {
    const totalSeconds = Math.floor(decimalHours * 3600);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}h${minutes.toString().padStart(2, '0')}m${seconds
        .toString()
        .padStart(2, '0')}s`;
}

export function EmployeeDashboard({ user }: EmployeeDashboardProps) {
    const [isClockedIn, setIsClockedIn] = useState(false);
    const [todayHours, setTodayHours] = useState(0);
    const [userClocks, setUserClocks] = useState<Clocks[]>([]);
    const [pairClocks, setPairClocks] = useState<
        { clockIn: Clocks; clockOut: Clocks | null; duration: number | null }[]
    >([]);
    const [totalWeekHours, setTotalWeekHours] = useState(0);

    const handleClockIn = async () => {
        try {
            await ClockService.createClock(new Clocks(0, user.id, 'IN', new Date()));
            setIsClockedIn(true);
            toast.success('Pointage d\'entrée enregistré avec succès');
            await fetchUserClocks();
        } catch (error) {
            toast.error('Échec de l\'enregistrement du pointage d\'entrée');
        }
    };

    const handleClockOut = async () => {
        try {
            await ClockService.createClock(new Clocks(0, user.id, 'OUT', new Date()));
            setIsClockedIn(false);
            toast.success('Pointage de sortie enregistré avec succès');
            await fetchUserClocks();
        } catch (error) {
            toast.error('Échec de l\'enregistrement du pointage de sortie');
        }
    };

    const fetchUserClocks = async () => {
        try {
            const result = await ClockService.getClocksByUserId(user.id);
            setUserClocks(result);

            const sorted = [...result].sort(
                (a, b) =>
                    utcToLocal(a.createdAt).getTime() -
                    utcToLocal(b.createdAt).getTime()
            );

            const paired: {
                clockIn: Clocks;
                clockOut: Clocks | null;
                duration: number | null;
            }[] = [];

            for (let i = 0; i < sorted.length; i++) {
                const clockIn = sorted[i];
                if (clockIn.type === 'IN') {
                    const clockOut = sorted.slice(i + 1).find((c) => c.type === 'OUT');
                    const duration = clockOut
                        ? ClockService.getDifferenceInHours(
                            utcToLocal(clockIn.createdAt),
                            utcToLocal(clockOut.createdAt)
                        )
                        : null;
                    if (clockOut) paired.push({ clockIn, clockOut, duration });
                    if (clockOut) i = sorted.indexOf(clockOut);
                }
            }

            setPairClocks(paired);

            const today = new Date();
            const todayTotal = paired
                .filter(
                    (s) =>
                        utcToLocal(s.clockIn.createdAt).toDateString() ===
                        today.toDateString()
                )
                .reduce((sum, s) => sum + (s.duration || 0), 0);
            setTodayHours(todayTotal);

            const { startWeek, endWeek } = ClockService.getCurrentWeekRange();
            const totalWeek = paired
                .filter((s) => {
                    const date = utcToLocal(s.clockIn.createdAt);
                    return date >= startWeek && date <= endWeek;
                })
                .reduce((sum, s) => sum + (s.duration || 0), 0);
            setTotalWeekHours(totalWeek);

            const last = sorted[sorted.length - 1];
            setIsClockedIn(last?.type === 'IN');
        } catch (err) {
            toast.error('Échec de la récupération des pointages');
        }
    };

    useEffect(() => {
        fetchUserClocks();
    }, []);

    useEffect(() => {
        if (!isClockedIn) return;

        const interval = setInterval(async () => {
            const result = await ClockService.getClocksByUserId(user.id);
            const lastIn = result.filter((c) => c.type === 'IN').slice(-1)[0];
            if (!lastIn) return;

            const now = new Date();

            const activeSessionHours = ClockService.getDifferenceInHours(
                utcToLocal(lastIn.createdAt),
                now
            );

            const sorted = [...result].sort(
                (a, b) =>
                    utcToLocal(a.createdAt).getTime() -
                    utcToLocal(b.createdAt).getTime()
            );

            const paired: {
                clockIn: Clocks;
                clockOut: Clocks | null;
                duration: number | null;
            }[] = [];

            for (let i = 0; i < sorted.length; i++) {
                const clockIn = sorted[i];
                if (clockIn.type === 'IN') {
                    const clockOut = sorted.slice(i + 1).find((c) => c.type === 'OUT');
                    const duration = clockOut
                        ? ClockService.getDifferenceInHours(
                            utcToLocal(clockIn.createdAt),
                            utcToLocal(clockOut.createdAt)
                        )
                        : null;
                    if (clockOut) paired.push({ clockIn, clockOut, duration });
                    if (clockOut) i = sorted.indexOf(clockOut);
                }
            }

            const today = new Date();
            const completedToday = paired
                .filter(
                    (s) =>
                        utcToLocal(s.clockIn.createdAt).toDateString() ===
                        today.toDateString()
                )
                .reduce((sum, s) => sum + (s.duration || 0), 0);

            setTodayHours(completedToday + activeSessionHours);
        }, 1000);

        return () => clearInterval(interval);
    }, [isClockedIn, user.id]);

    return (
        <div className="space-y-6">
            <div>
                <h1>Bienvenue, {user.firstName} !</h1>
                <p className="text-muted-foreground">
                    Gérez vos heures de travail et consultez votre historique d'activité
                </p>
            </div>

            {/* Clock In/Out Section */}
            <div className="grid gap-6 md:grid-cols-2">
                <Card className="border-2">
                    <CardHeader>
                        <CardTitle>Suivi du temps</CardTitle>
                        <CardDescription>Enregistrez votre entrée ou sortie</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-muted-foreground">Statut actuel</p>
                                <div className="flex items-center gap-2 mt-1">
                                    <Badge variant={isClockedIn ? 'default' : 'secondary'}>
                                        {isClockedIn ? 'En service' : 'Hors service'}
                                    </Badge>
                                </div>
                            </div>
                            <Clock className="h-8 w-8 text-muted-foreground" />
                        </div>

                        <div className="flex gap-2">
                            <Button
                                className="flex-1 gap-2"
                                onClick={handleClockIn}
                                disabled={isClockedIn}
                                variant={isClockedIn ? 'secondary' : 'default'}
                            >
                                <LogIn className="h-4 w-4" />
                                Entrée
                            </Button>
                            <Button
                                className="flex-1 gap-2"
                                onClick={handleClockOut}
                                disabled={!isClockedIn}
                                variant={!isClockedIn ? 'secondary' : 'default'}
                            >
                                <LogOut className="h-4 w-4" />
                                Sortie
                            </Button>
                        </div>

                        {isClockedIn && (
                            <div className="p-4 bg-muted rounded-lg">
                                <p className="text-muted-foreground">Temps écoulé aujourd'hui</p>
                                <p className="text-2xl mt-1 font-mono">
                                    {decimalToHMS(todayHours)}
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Résumé des heures</CardTitle>
                        <CardDescription>Vue d'ensemble de votre temps de travail</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Aujourd'hui</span>
                                </div>
                                <span>{decimalToHMS(todayHours)}</span>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Cette semaine</span>
                                </div>
                                <span>{decimalToHMS(totalWeekHours)}</span>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                                <span className="text-muted-foreground">Moyenne quotidienne</span>
                                <span className="text-lg">
                                    {decimalToHMS(totalWeekHours / 5 || 0)}
                                </span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Historique des pointages</CardTitle>
                    <CardDescription>Vos derniers enregistrements de présence</CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Entrée</TableHead>
                                <TableHead>Sortie</TableHead>
                                <TableHead className="text-right">Durée</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {pairClocks
                                .sort((a, b) =>
                                    utcToLocal(b.clockIn.createdAt).getTime() -
                                    utcToLocal(a.clockIn.createdAt).getTime()
                                )
                                .slice(0, 8)
                                .map((session, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>
                                            {utcToLocal(session.clockIn.createdAt).toLocaleDateString(
                                                'fr-FR',
                                                {
                                                    weekday: 'short',
                                                    day: 'numeric',
                                                    month: 'short',
                                                    timeZone: 'Europe/Paris',
                                                }
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {utcToLocal(session.clockIn.createdAt).toLocaleTimeString(
                                                'fr-FR',
                                                {
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    second: '2-digit',
                                                    timeZone: 'Europe/Paris',
                                                }
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {session.clockOut
                                                ? utcToLocal(
                                                    session.clockOut.createdAt
                                                ).toLocaleTimeString('fr-FR', {
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    second: '2-digit',
                                                    timeZone: 'Europe/Paris',
                                                })
                                                : '-'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {session.duration
                                                ? decimalToHMS(session.duration)
                                                : '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
